import { Component } from './component';

export type MenuItem =
  | {
      label: string;
      shortcut?: string;
      action?: () => void;
      checkbox?: {
        checked: () => boolean;
        toggle: () => void;
      };
      radio?: {
        name: string;
        value: string;
        selected: (value: string) => boolean;
        select: (value: string) => void;
      };
      submenu?: MenuItem[];
    }
  | 'separator';

export interface MenuProps {
  items: MenuItem[];
}

export class Menu extends Component {
  public el: HTMLMenuElement;

  constructor(props: MenuProps) {
    super();
    this.el = document.createElement('menu');
    this.el.role = 'menu';

    const buildMenu = (
      items: MenuItem[],
      parentEl: HTMLElement,
      topLevel = false,
    ) => {
      for (const item of items) {
        if (item === 'separator') {
          const hr = document.createElement('hr');
          hr.role = 'separator';
          parentEl.appendChild(hr);
          continue;
        }

        const li = document.createElement('li');
        li.role = 'menuitem';

        const label = document.createElement('div');
        label.innerText = item.label;
        li.appendChild(label);

        if (item.shortcut) {
          const shortcut = document.createElement('div');
          shortcut.classList.add('shortcut');
          shortcut.innerText = item.shortcut;
          li.appendChild(shortcut);
        }

        if (item.checkbox) {
          li.classList.add('checkbox');

          if (item.checkbox.checked()) {
            li.classList.add('checked');
          }

          li.addEventListener('pointerdown', (e) => {
            if (e.button !== 0 && e.pointerType !== 'touch') return;
            li.classList.toggle('checked');
            item.checkbox?.toggle();
            e.stopPropagation();
          });

          parentEl.appendChild(li);
          continue;
        }

        if (item.radio) {
          li.classList.add('radio');

          if (item.radio.selected(item.radio.value)) {
            li.classList.add('selected');
          }

          li.addEventListener('pointerdown', (e) => {
            if (e.button !== 0 && e.pointerType !== 'touch') return;
            const siblingRadios = li.parentElement?.querySelectorAll(
              ':scope > li.radio.selected',
            );
            siblingRadios?.forEach((sibling) => {
              sibling.classList.remove('selected');
            });
            li.classList.add('selected');
            item.radio?.select(item.radio.value);
            e.stopPropagation();
          });

          parentEl.appendChild(li);
          continue;
        }

        if (item.submenu) {
          const submenuEl = document.createElement('menu');
          submenuEl.role = 'sub-menu';
          li.classList.add('has-submenu');
          buildMenu(item.submenu, submenuEl);
          li.appendChild(submenuEl);

          const closeSiblings = () => {
            const siblingMenus =
              li.parentElement?.querySelectorAll(':scope > li.open');
            siblingMenus?.forEach((sibling) => {
              if (sibling !== li) {
                sibling.classList.remove('open');
              }
            });
          };

          li.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            li.classList.toggle('open');
            closeSiblings();
          });

          li.addEventListener('pointerenter', (e) => {
            const anyTopLiOpen = Array.from(
              li.parentElement?.children || [],
            ).some(
              (sibling) => sibling !== li && sibling.classList.contains('open'),
            );
            if (topLevel && !anyTopLiOpen) return;
            e.stopPropagation();
            li.classList.add('open');
            closeSiblings();
          });

          li.addEventListener('pointerleave', (e) => {
            if (topLevel) return;
            e.stopPropagation();
            li.classList.remove('open');
            closeSiblings();
          });

          parentEl.appendChild(li);
          continue;
        }

        li.addEventListener('pointerdown', (e) => {
          e.stopPropagation();
        });

        li.addEventListener('pointerup', () => {
          const openMenus = this.el.querySelectorAll('li.open');
          for (const menu of openMenus) {
            menu.classList.remove('open');
          }

          setTimeout(() => {
            if (item.action) {
              item.action();
            }
          }, 10);
        });

        parentEl.appendChild(li);
      }
    };

    buildMenu(props.items, this.el, true);

    // Close all submenus when clicking outside
    document.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 && e.pointerType !== 'touch') return;
      const target = e.target as HTMLElement;
      const menu = target.closest('menu[role="menu"]');

      if (!menu || menu !== this.el) {
        const openMenus = this.el.querySelectorAll('li.open');
        for (const menu of openMenus) {
          menu.classList.remove('open');
        }
      }
    });
  }
}
